import React, { useMemo } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { useAuth } from "../../contexts/AuthContext";
import {
  batteryToFormState
} from "../../utils/batteryUtils";

const formatDate = (data) => {
  if (!data) return "-";
  const splitT = String(data).split("T");
  const split = splitT[0].split("-");
  if (split.length !== 3) return data;
  return `${split[2]}/${split[1]}/${split[0]}`;
};

const boolLabel = (value) => {
  if (value === true || value === 1 || value === "1") return "Sim";
  if (value === false || value === 0 || value === "0") return "Não";
  return "-";
};

const DetailItem = ({ label, value, multiline = false }) => {
  const displayValue = value === 0 ? "0" : value || "-";
  return (
    <div className="mb-2">
      <strong>{label}: </strong>
      {multiline ? (
        <p className="mb-0 mt-1" style={{ whiteSpace: "pre-wrap" }}>
          {displayValue}
        </p>
      ) : (
        <span>{displayValue}</span>
      )}
    </div>
  );
};

const getReportNumber = (report) => {
  if (!report) return "-";
  if (report.auto_report) {
    return String(report.auto_report).padStart(6, "0");
  }
  if (report.report) {
    return String(report.report).padStart(6, "0");
  }
  return "-";
};

const getServiceCallNumber = (serviceCall) => {
  if (!serviceCall?.service_number) return "-";
  const year =
    serviceCall?.service_date?.split("-")[0] || new Date().getFullYear();
  return `${year}.${serviceCall.service_number}`;
};

const getPreventiveTypeLabel = (preventiveType) => {
  if (!preventiveType) return "Corretiva";
  const typeArr = preventiveType.match(/[A-Z][a-z]+/g);
  if (!typeArr) return preventiveType;
  return typeArr
    .map((type) => {
      if (type === "Combustao") return "Combustão";
      if (type === "Eletrica") return "Elétrica";
      if (type === "Retratil") return "Retrátil";
      return type;
    })
    .join(" ");
};

const getBatteriesFromReport = (report, isGLP) => {
  if (isGLP || !report) return [];
  const batteriesFromApi = report?.batteries;
  if (batteriesFromApi?.length > 0) {
    return batteriesFromApi.map((battery) => batteryToFormState(battery));
  }
  if (report?.brand || report?.pat || report?.manufacture_date) {
    return [batteryToFormState(report)];
  }
  return [];
};

const BatterySection = ({ batteries }) => {
  if (!batteries.length) return null;

  const hasData = batteries.some(
    (bat) =>
      bat.brand ||
      bat.totalVoltage ||
      bat.elements.some((el) => el.voltage || el.density)
  );
  if (!hasData) return null;

  return (
    <div className="mt-4">
      <h5>
        Medição de Bateria
        {batteries.length > 1 ? ` (${batteries.length} baterias)` : ""}
      </h5>
      {batteries.map((bat, batIdx) => (
        <div key={batIdx} className="border rounded p-3 mb-3">
          <h6 className="mb-3">
            {batteries.length > 1
              ? `Bateria ${batIdx + 1}`
              : "Dados da bateria"}
          </h6>
          <div className="row">
            <div className="col-md-3 mb-2">
              <DetailItem label="PAT" value={bat.brand} />
            </div>
            <div className="col-md-3 mb-2">
              <DetailItem
                label="Tensão total (V)"
                value={bat.totalVoltage}
              />
            </div>
          </div>
          <div className="row mt-2">
            {bat.elements.slice(0, bat.elementCount).map((el, idx) => (
              <div key={idx} className="col-md-2 mb-2">
                <div className="border rounded p-2">
                  <div className="fw-bold small mb-1">Elem. {idx + 1}</div>
                  <div className="small">V: {el.voltage || "-"}</div>
                  <div className="small">Dens.: {el.density || "-"}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

const ReportDetails = ({
  visible = false,
  onHide = () => {},
  report = null,
  loading = false
}) => {
  const { access_type } = useAuth();
  const showInternalNotes = access_type !== "Costumer";

  const isCorrective = Boolean(report?.service_call);
  const isGLP =
    report?.preventive_type === "EmpilhadeiraCombustao" ||
    report?.preventive_type === "EmpilhadeiraEletricaDeContrapeso";

  const batteries = useMemo(
    () => getBatteriesFromReport(report, isGLP),
    [report, isGLP]
  );

  const equipment =
    report?.service_call?.equipment?.equipment ||
    report?.equipment?.equipment ||
    "-";

  const secondTechName =
    report?.second_tech_user?.name ||
    report?.second_technician?.name ||
    report?.second_tech_name ||
    "-";

  const attendance = report?.attendance
    ? String(report.attendance).replace("-", " - ")
    : "-";

  const headerTitle = isCorrective
    ? "Detalhes da Corretiva"
    : "Detalhes da Preventiva";

  const footer = (
    <Button
      label="Fechar"
      icon="pi pi-times"
      className="p-button-text"
      onClick={onHide}
    />
  );

  return (
    <Dialog
      header={headerTitle}
      visible={visible}
      style={{ width: "80vw", maxWidth: "90rem" }}
      onHide={onHide}
      baseZIndex={1000}
      footer={footer}
      modal
    >
      {loading ? (
        <div className="d-flex justify-content-center align-items-center py-5">
          <i
            className="pi pi-spin pi-spinner me-2"
            style={{ fontSize: "1.5rem" }}
          />
          Carregando...
        </div>
      ) : (
        report && (
          <div className="container-fluid">
            <div className="row mb-4">
              <div className="col-md-6">
                <h5>Informações Gerais</h5>
                <DetailItem label="Relatório" value={getReportNumber(report)} />
                {isCorrective && (
                  <DetailItem
                    label="Chamado"
                    value={getServiceCallNumber(report.service_call)}
                  />
                )}
                <DetailItem
                  label="Tipo"
                  value={getPreventiveTypeLabel(report.preventive_type)}
                />
                <DetailItem label="Empresa" value={report.company?.name} />
                <DetailItem label="Equipamento" value={equipment} />
                <DetailItem label="Data" value={formatDate(report.date)} />
                <DetailItem
                  label="Horário de atendimento"
                  value={attendance}
                />
                <DetailItem
                  label="Criado por"
                  value={report.created_by?.name || report.creator?.name}
                />
                <DetailItem label="2º técnico" value={secondTechName} />
              </div>
              <div className="col-md-6">
                <h5>Dados do Atendimento</h5>
                {isCorrective ? (
                  <>
                    <DetailItem
                      label="Descrição"
                      value={report.error_description}
                      multiline
                    />
                    <DetailItem label="Quilometragem" value={report.mileage} />
                    <DetailItem label="Horímetro" value={report.hour_meter} />
                    <DetailItem label="Causa" value={report.cause} multiline />
                    <DetailItem
                      label="Solução"
                      value={report.solution}
                      multiline
                    />
                    <DetailItem
                      label="Houve pendência?"
                      value={boolLabel(report.has_pendency)}
                    />
                    {Boolean(report.has_pendency) && (
                      <DetailItem
                        label="Pendência"
                        value={report.pendency}
                        multiline
                      />
                    )}
                    <DetailItem
                      label="Teste efetuado?"
                      value={boolLabel(report.testeEfetuado)}
                    />
                    <DetailItem
                      label="Mau uso?"
                      value={boolLabel(report.misuse)}
                    />
                    <DetailItem
                      label="Resultado positivo?"
                      value={boolLabel(report.resultadoPositivo)}
                    />
                    <DetailItem
                      label="Aplicação de peças?"
                      value={boolLabel(report.aplicacaoDePecas)}
                    />
                    <DetailItem
                      label="Nome do cliente"
                      value={
                        report.costumer_name ||
                        report.service_call?.company?.contact_name
                      }
                    />
                    <DetailItem
                      label="E-mail do cliente"
                      value={
                        report.costumer_email ||
                        report.service_call?.company?.email
                      }
                    />
                  </>
                ) : (
                  <>
                    <DetailItem
                      label="Observações"
                      value={report.comments}
                      multiline
                    />
                    <DetailItem label="Horímetro" value={report.hour_meter} />
                  </>
                )}
                {showInternalNotes && (
                  <DetailItem
                    label="Informações de uso interno"
                    value={report.internal_notes}
                    multiline
                  />
                )}
              </div>
            </div>
            <BatterySection batteries={batteries} />
          </div>
        )
      )}
    </Dialog>
  );
};

export default ReportDetails;
